/**
 * Enhanced logo management system with local caching and logoapi integration
 */

// IndexedDB setup for logo caching
const DB_NAME = 'LogoCache';
const DB_VERSION = 1;
const STORE_NAME = 'logos';
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

interface CachedLogo {
  id: string;
  url: string;
  blob?: Blob;
  timestamp: number;
  source: 'logoapi' | 'facr' | 'upload';
  lastUsed: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

const initDB = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;
  
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('lastUsed', 'lastUsed', { unique: false });
        store.createIndex('source', 'source', { unique: false });
      }
    };
  });
  
  return dbPromise;
};

/**
 * Get logo from cache
 */
const getCachedLogo = async (id: string): Promise<CachedLogo | null> => {
  try {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    return new Promise((resolve) => {
      const request = store.get(id);
      request.onsuccess = () => {
        const logo = request.result as CachedLogo | undefined;
        if (!logo) {
          resolve(null);
          return;
        }
        
        // Check if cache is still valid
        const now = Date.now();
        if (now - logo.timestamp > CACHE_DURATION) {
          resolve(null);
          return;
        }
        
        resolve(logo);
      };
      request.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
};

/**
 * Save logo to cache
 */
const saveCachedLogo = async (logo: CachedLogo): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put(logo);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn('Failed to cache logo:', e);
  }
};

/**
 * Update last used timestamp
 */
const updateLastUsed = async (id: string): Promise<void> => {
  try {
    const cached = await getCachedLogo(id);
    if (cached) {
      cached.lastUsed = Date.now();
      await saveCachedLogo(cached);
    }
  } catch {}
};

/**
 * Clean up unused logos from cache
 */
export const cleanupUnusedLogos = async (): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('lastUsed');
    
    const now = Date.now();
    const threshold = now - (30 * 24 * 60 * 60 * 1000); // 30 days
    
    const request = index.openCursor();
    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result;
      if (cursor) {
        const logo = cursor.value as CachedLogo;
        if (logo.lastUsed < threshold) {
          cursor.delete();
        }
        cursor.continue();
      }
    };
  } catch (e) {
    console.warn('Failed to cleanup logos:', e);
  }
};

/**
 * Optimize SVG by removing whitespace and fixing viewBox
 * This aggressively removes padding/empty space around logos
 */
const optimizeSVG = async (svgUrl: string): Promise<string> => {
  try {
    const response = await fetch(svgUrl);
    if (!response.ok) return svgUrl;
    
    let svgText = await response.text();
    
    // Parse SVG to manipulate it
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, 'image/svg+xml');
    const svg = doc.documentElement as unknown as SVGSVGElement;
    
    // Remove XML declarations and comments
    svgText = svgText.replace(/<\?xml[^?]*\?>/g, '');
    svgText = svgText.replace(/<!--[\s\S]*?-->/g, '');
    
    // Create a temporary SVG in the DOM to get proper bounding box
    if (typeof document !== 'undefined') {
      const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      tempSvg.innerHTML = svg.innerHTML;
      tempSvg.style.position = 'absolute';
      tempSvg.style.visibility = 'hidden';
      tempSvg.style.width = '1000px';
      tempSvg.style.height = '1000px';
      document.body.appendChild(tempSvg);
      
      try {
        // Get bounding box of all content
        const bbox = tempSvg.getBBox();
        if (bbox && bbox.width > 0 && bbox.height > 0) {
          // Add minimal padding (2% of size) for visual comfort
          const padding = Math.max(bbox.width, bbox.height) * 0.02;
          const x = bbox.x - padding;
          const y = bbox.y - padding;
          const width = bbox.width + (padding * 2);
          const height = bbox.height + (padding * 2);
          
          // Set viewBox to the actual content bounds (removes empty space)
          svg.setAttribute('viewBox', `${x} ${y} ${width} ${height}`);
          svg.removeAttribute('width');
          svg.removeAttribute('height');
          
          // Ensure preserveAspectRatio is set correctly
          svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        }
      } catch (e) {
        // getBBox can fail in some cases, try alternative approach
        console.warn('getBBox failed, using fallback optimization:', e);
        // Fallback: just ensure preserveAspectRatio is correct
        svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        svg.removeAttribute('width');
        svg.removeAttribute('height');
      } finally {
        document.body.removeChild(tempSvg);
      }
      
      // Serialize back to string
      const serializer = new XMLSerializer();
      svgText = serializer.serializeToString(svg);
    }
    
    // Create a data URL from the optimized SVG
    const blob = new Blob([svgText], { type: 'image/svg+xml' });
    return URL.createObjectURL(blob);
  } catch (e) {
    console.warn('Failed to optimize SVG:', e);
    return svgUrl;
  }
};

// Circuit breaker state for logo API requests
let circuitBreakerState = {
  failures: 0,
  lastFailureTime: 0,
  isOpen: false,
  maxFailures: 5,
  resetTimeout: 60000, // 1 minute
};

/**
 * Check if circuit breaker is open
 */
const isCircuitBreakerOpen = (): boolean => {
  const now = Date.now();
  if (circuitBreakerState.isOpen && now - circuitBreakerState.lastFailureTime > circuitBreakerState.resetTimeout) {
    // Reset circuit breaker
    circuitBreakerState.isOpen = false;
    circuitBreakerState.failures = 0;
    return false;
  }
  return circuitBreakerState.isOpen;
};

/**
 * Record circuit breaker failure
 */
const recordCircuitBreakerFailure = (): void => {
  circuitBreakerState.failures++;
  circuitBreakerState.lastFailureTime = Date.now();
  if (circuitBreakerState.failures >= circuitBreakerState.maxFailures) {
    circuitBreakerState.isOpen = true;
    console.warn('Logo API circuit breaker opened due to repeated failures');
  }
};

/**
 * Record circuit breaker success
 */
const recordCircuitBreakerSuccess = (): void => {
  if (circuitBreakerState.failures > 0) {
    circuitBreakerState.failures = Math.max(0, circuitBreakerState.failures - 1);
  }
};

/**
 * Fetch logo from logoapi.sportcreative.eu with optimization and circuit breaker
 */
export const fetchLogoFromLogoAPI = async (teamId: string, teamName?: string): Promise<string | null> => {
  // Check circuit breaker first
  if (isCircuitBreakerOpen()) {
    console.warn(`Logo API circuit breaker is open, skipping request for team ${teamId}`);
    return null;
  }
  
  try {
    // Check cache first
    const cached = await getCachedLogo(teamId);
    if (cached?.url && !cached.url.startsWith('blob:')) {
      await updateLastUsed(teamId);
      return cached.url;
    }
    
    // Fetch from logoapi with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    const res = await fetch(`https://logoapi.sportcreative.eu/logos/${teamId}/json`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!res.ok) {
      recordCircuitBreakerFailure();
      return null;
    }
    
    const data = await res.json();
    const url = data.logo_url_svg || data.logo_url_png || data.logo_url;
    
    if (!url) {
      recordCircuitBreakerFailure();
      return null;
    }
    
    // Cache the logo
    await saveCachedLogo({
      id: teamId,
      url,
      timestamp: Date.now(),
      source: 'logoapi',
      lastUsed: Date.now(),
    });
    
    recordCircuitBreakerSuccess();
    return url;
  } catch (e) {
    recordCircuitBreakerFailure();
    console.warn(`Failed to fetch logo for team ${teamId}:`, e);
    return null;
  }
};

/**
 * Fetch club name and logo from logoapi.sportcreative.eu
 */
export const fetchClubNameAndLogoFromAPI = async (teamId: string): Promise<{ clubName?: string; logoUrl?: string } | null> => {
  try {
    // Fetch from logoapi
    const res = await fetch(`https://logoapi.sportcreative.eu/logos/${teamId}/json`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
    
    if (!res.ok) return null;
    
    const data = await res.json();
    const logoUrl = data.logo_url_svg || data.logo_url_png || data.logo_url;
    const clubName = data.club_name;
    
    if (!logoUrl && !clubName) return null;
    
    // Cache the logo if available
    if (logoUrl) {
      await saveCachedLogo({
        id: teamId,
        url: logoUrl,
        timestamp: Date.now(),
        source: 'logoapi',
        lastUsed: Date.now(),
      });
    }
    
    return {
      clubName: clubName || undefined,
      logoUrl: logoUrl || undefined,
    };
  } catch (e) {
    console.warn(`Failed to fetch club info for team ${teamId}:`, e);
    return null;
  }
};

// Request monitoring for logo API
let requestStats = {
  totalRequests: 0,
  successfulRequests: 0,
  failedRequests: 0,
  lastResetTime: Date.now(),
  windowMs: 60000, // 1 minute window
};

/**
 * Get current request statistics
 */
export const getLogoAPIRequestStats = () => {
  const now = Date.now();
  if (now - requestStats.lastResetTime > requestStats.windowMs) {
    // Reset stats if window expired
    requestStats = {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      lastResetTime: now,
      windowMs: requestStats.windowMs,
    };
  }
  return { ...requestStats };
};

/**
 * Track a logo API request
 */
const trackRequest = (success: boolean): void => {
  requestStats.totalRequests++;
  if (success) {
    requestStats.successfulRequests++;
  } else {
    requestStats.failedRequests++;
  }
  
  // Log warning if too many requests
  if (requestStats.totalRequests > 100) {
    console.warn(`High logo API request volume detected: ${requestStats.totalRequests} requests in last minute`);
  }
};

/**
 * Fetches team logos in bulk from logoapi.sportcreative.eu API
 * Enhanced with rate limiting and batching to prevent request storms
 * @param teamIds Array of team IDs to fetch logos for
 * @returns Promise that resolves to a map of teamId -> logo URL
 */
export const batchFetchLogosFromSportLogosAPI = async (teamIds: string[]): Promise<Record<string, string>> => {
  if (!teamIds.length) return {};
  
  // Check current request volume
  const stats = getLogoAPIRequestStats();
  if (stats.totalRequests > 200) {
    console.warn(`Request volume too high (${stats.totalRequests}/min). Skipping batch logo fetch.`);
    return {};
  }
  
  // Rate limiting: process in batches of 10 concurrent requests
  const BATCH_SIZE = 10;
  const RATE_LIMIT_DELAY = 100; // ms between batches
  
  try {
    const logos: Record<string, string> = {};
    
    // Process in batches to avoid overwhelming the API
    for (let i = 0; i < teamIds.length; i += BATCH_SIZE) {
      const batch = teamIds.slice(i, i + BATCH_SIZE);
      
      // Process current batch concurrently
      const batchPromises = batch.map(async (id) => {
        // Check cache first to avoid unnecessary requests
        const cached = await getCachedLogo(id);
        if (cached?.url && !cached.url.startsWith('blob:')) {
          await updateLastUsed(id);
          return { id, url: cached.url, cached: true };
        }
        
        const url = await fetchLogoFromLogoAPI(id);
        trackRequest(!!url);
        return { id, url, cached: false };
      });
      
      const batchResults = await Promise.all(batchPromises);
      batchResults.forEach(({ id, url, cached }) => {
        if (url) logos[id] = url;
      });
      
      // Rate limiting delay between batches (except for last batch)
      if (i + BATCH_SIZE < teamIds.length) {
        await new Promise(resolve => setTimeout(resolve, RATE_LIMIT_DELAY));
      }
    }
    
    return logos;
  } catch (error) {
    console.error('Error in batchFetchLogosFromSportLogosAPI (logoapi.sportcreative.eu):', error);
    return {};
  }
};

/**
 * Get logo for a team by name or ID, with fallback chain:
 * 1. logoapi.sportcreative.eu (cached)
 * 2. FACR logo (if provided) - this may be a fotbal.cz URL or local upload
 * 3. If local upload fails, try to construct fotbal.cz URL from team ID
 * 4. Placeholder
 */
export const getTeamLogo = async (teamId?: string, teamName?: string, facrLogo?: string): Promise<string> => {
  const PLACEHOLDER = '/logo192.png';
  
  // Try logoapi first if we have an ID
  if (teamId) {
    const logoApiUrl = await fetchLogoFromLogoAPI(teamId, teamName);
    if (logoApiUrl) return logoApiUrl;
  }
  
  // Fallback to FACR logo (this may be a fotbal.cz URL or local upload)
  if (facrLogo) {
    // If it's already a fotbal.cz URL, use it directly
    if (facrLogo.includes('fotbal.cz') || facrLogo.includes('is1.fotbal.cz')) {
      return facrLogo;
    }
    
    // If it's a local upload, we'll let the browser handle it and fall back to fotbal.cz on 404
    if (facrLogo.startsWith('/uploads/logos/facr/')) {
      return facrLogo; // Let the browser handle 404 -> fallback
    }
    
    // Otherwise return as-is
    return facrLogo;
  }
  
  // If we have a team ID but no FACR logo, try to construct fotbal.cz URL
  if (teamId) {
    const fotbalCzUrl = `https://is1.fotbal.cz/media/kluby/${teamId}/${teamId}_crop.jpg`;
    return fotbalCzUrl;
  }
  
  // Return placeholder
  return PLACEHOLDER;
};
